<template>
    <div class="ul-app">

        <!-- ░░ AMBIENT BACKGROUND ░░ -->
        <div class="ul-ambient" aria-hidden="true">
            <div class="ul-orb ul-orb-1"></div>
            <div class="ul-orb ul-orb-2"></div>
            <div class="ul-orb ul-orb-3"></div>
            <div class="ul-grid-dots"></div>
        </div>

        <!-- ░░ SIDEBAR ░░ -->
        <aside class="ul-sidebar" :class="{'ul-sidebar--open': drawerOpen}" role="navigation" aria-label="Main navigation">

            <!-- Logo -->
            <div class="ul-sb-logo">
                <Link href="/user/dashboard" class="ul-logo-link">
                    <img v-if="logoUrl && !logoErr" :src="logoUrl" @error="logoErr=true" class="ul-logo-img" alt="Logo" />
                    <span v-else class="ul-logo-text">{{ appName }}</span>
                </Link>
            </div>

            <!-- Main Nav -->
            <div class="ul-sb-nav-wrap">
                <p class="ul-sb-section-lbl">MAIN MENU</p>
                <nav class="ul-sb-nav">
                    <Link v-for="item in mainNavItems" :key="item.url"
                          :href="item.url"
                          class="ul-sb-item"
                          :class="isActive(item.url) ? 'ul-sb-item--active' : ''"
                          @click="drawerOpen=false">
                        <span class="ul-sb-icon" v-html="item.icon"></span>
                        <span class="ul-sb-label">{{ item.label }}</span>
                        <span v-if="item.badge" class="ul-sb-badge">{{ item.badge }}</span>
                    </Link>
                </nav>

                <p class="ul-sb-section-lbl" style="margin-top:20px">BINARY MLM</p>
                <nav class="ul-sb-nav">
                    <Link v-for="item in binaryNavItems" :key="item.url"
                          :href="item.url"
                          class="ul-sb-item"
                          :class="isActive(item.url) ? 'ul-sb-item--active' : ''"
                          @click="drawerOpen=false">
                        <span class="ul-sb-icon" v-html="item.icon"></span>
                        <span class="ul-sb-label">{{ item.label }}</span>
                    </Link>
                </nav>

                <p class="ul-sb-section-lbl" style="margin-top:20px">ACCOUNT</p>
                <nav class="ul-sb-nav">
                    <Link href="/user/settings"
                          class="ul-sb-item"
                          :class="isActive('/user/settings') ? 'ul-sb-item--active' : ''"
                          @click="drawerOpen=false">
                        <span class="ul-sb-icon" v-html="icons.settings"></span>
                        <span class="ul-sb-label">Settings</span>
                    </Link>
                    <Link href="/user/settings/wallets"
                          class="ul-sb-item"
                          :class="isActive('/user/settings/wallets') ? 'ul-sb-item--active' : ''"
                          @click="drawerOpen=false">
                        <span class="ul-sb-icon" v-html="icons.walletBook"></span>
                        <span class="ul-sb-label">Wallet Book</span>
                    </Link>
                    <Link href="/user/support-tickets"
                          class="ul-sb-item"
                          :class="isActive('/user/support-tickets') ? 'ul-sb-item--active' : ''"
                          @click="drawerOpen=false">
                        <span class="ul-sb-icon" v-html="icons.support"></span>
                        <span class="ul-sb-label">Support</span>
                    </Link>
                </nav>
            </div>

            <!-- Bottom: Logout + User chip -->
            <div class="ul-sb-bottom">
                <Link href="/logout" method="post" class="ul-sb-logout" @click="drawerOpen=false">
                    <span class="ul-sb-icon" v-html="icons.logout"></span>
                    <span>Log Out</span>
                </Link>
                <div class="ul-sb-user" v-if="authUser">
                    <div class="ul-sb-avatar">{{ userInitials }}</div>
                    <div class="ul-sb-user-info">
                        <p class="ul-sb-user-name">{{ authUser.display_name || authUser.name || 'WalletConnect User' }}</p>
                        <p class="ul-sb-user-email">{{ authUser.email }}</p>
                        <p v-if="authUser.uid" class="ul-sb-user-uid">{{ authUser.uid }}</p>
                    </div>
                </div>
            </div>
        </aside>

        <!-- Mobile overlay -->
        <Transition name="ul-fade">
            <div v-if="drawerOpen" class="ul-overlay" @click="drawerOpen=false" aria-hidden="true"></div>
        </Transition>

        <!-- ░░ MAIN AREA ░░ -->
        <div class="ul-main">

            <!-- Top Bar -->
            <header class="ul-topbar" role="banner">
                <!-- Hamburger (mobile) -->
                <button class="ul-hamburger" @click="drawerOpen=!drawerOpen" aria-label="Toggle navigation" :aria-expanded="drawerOpen">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                        <path v-if="!drawerOpen" d="M4 6h16M4 12h16M4 18h16"/>
                        <path v-else d="M6 18L18 6M6 6l12 12"/>
                    </svg>
                </button>

                <!-- Page title (desktop) -->
                <div class="ul-tb-title">
                    <h1 class="ul-tb-h1">{{ pageTitle }}</h1>
                    <p class="ul-tb-sub" v-if="pageSection">{{ pageSection }}</p>
                </div>

                <!-- Spacer on mobile -->
                <div class="ul-tb-spacer"></div>

                <!-- Right controls -->
                <div class="ul-tb-right">
                    <!-- Status pill -->
                    <div class="ul-status-pill">
                        <span class="ul-status-dot"></span>
                        <span class="ul-status-text">Platform Active</span>
                    </div>

                    <!-- Notification bell -->
                    <button class="ul-icon-btn" aria-label="Notifications">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                            <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
                        </svg>
                        <span class="ul-notif-dot"></span>
                    </button>

                    <!-- Avatar + dropdown -->
                    <div class="ul-avatar-wrap" ref="avatarRef">
                        <button class="ul-avatar-btn" @click="userMenuOpen=!userMenuOpen" :aria-expanded="userMenuOpen" aria-label="User menu">
                            <div class="ul-tb-avatar">{{ userInitials }}</div>
                        </button>

                        <Transition name="ul-drop">
                            <div v-if="userMenuOpen" class="ul-user-drop">
                                <div class="ul-drop-header">
                                    <div class="ul-drop-avatar">{{ userInitials }}</div>
                                    <div>
                                        <p class="ul-drop-name">{{ authUser?.display_name || authUser?.name || 'WalletConnect User' }}</p>
                                        <p class="ul-drop-email">{{ authUser?.email }}</p>
                                        <p v-if="authUser?.uid" class="ul-drop-uid">{{ authUser.uid }}</p>
                                    </div>
                                </div>
                                <div class="ul-drop-wallet" v-if="wallet">
                                    <div class="ul-drop-stat">
                                        <span class="ul-drop-stat-lbl">Balance</span>
                                        <span class="ul-drop-stat-val">{{ currencySymbol }}{{ fmtBalance(wallet.balance) }}</span>
                                    </div>
                                    <div class="ul-drop-stat">
                                        <span class="ul-drop-stat-lbl">Earnings</span>
                                        <span class="ul-drop-stat-val" style="color:#a75ded">{{ currencySymbol }}{{ fmtBalance(wallet.total_earnings) }}</span>
                                    </div>
                                </div>
                                <div class="ul-drop-menu">
                                    <Link href="/user/settings" class="ul-drop-item" @click="userMenuOpen=false">
                                        <span v-html="icons.settings" style="width:16px;height:16px;display:inline-flex"></span>
                                        Settings
                                    </Link>
                                    <Link href="/user/support-tickets" class="ul-drop-item" @click="userMenuOpen=false">
                                        <span v-html="icons.support" style="width:16px;height:16px;display:inline-flex"></span>
                                        Support
                                    </Link>
                                    <div class="ul-drop-divider"></div>
                                    <Link href="/logout" method="post" class="ul-drop-item ul-drop-logout" @click="userMenuOpen=false">
                                        <span v-html="icons.logout" style="width:16px;height:16px;display:inline-flex"></span>
                                        Sign Out
                                    </Link>
                                </div>
                            </div>
                        </Transition>
                    </div>
                </div>
            </header>

            <!-- Page content -->
            <main class="ul-content" role="main">
                <slot />
            </main>
        </div>

        <ToastContainer />

        <!-- ── MOBILE BOTTOM NAV (hidden on desktop ≥1024px) ── -->
        <nav class="ul-mob-nav" aria-label="Bottom navigation">
            <Link href="/user/dashboard" class="ul-mob-tab" :class="isActive('/user/dashboard') ? 'ul-mob-tab--on' : ''">
                <div class="ul-mob-ic" :class="isActive('/user/dashboard') ? 'ul-mob-ic--on' : ''">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/></svg>
                </div>
                <span>Home</span>
            </Link>
            <Link href="/user/investments" class="ul-mob-tab" :class="isActive('/user/investments') ? 'ul-mob-tab--on' : ''">
                <div class="ul-mob-ic" :class="isActive('/user/investments') ? 'ul-mob-ic--on' : ''">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
                </div>
                <span>Invest</span>
            </Link>
            <Link href="/user/finance" class="ul-mob-tab ul-mob-tab--plus">
                <div class="ul-mob-plus">
                    <svg width="24" height="24" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
                </div>
                <span>Deposit</span>
            </Link>
            <Link href="/user/referral" class="ul-mob-tab" :class="isActive('/user/referral') ? 'ul-mob-tab--on' : ''">
                <div class="ul-mob-ic" :class="isActive('/user/referral') ? 'ul-mob-ic--on' : ''">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                </div>
                <span>Network</span>
            </Link>
            <Link href="/user/settings" class="ul-mob-tab" :class="isActive('/user/settings') ? 'ul-mob-tab--on' : ''">
                <div class="ul-mob-ic" :class="isActive('/user/settings') ? 'ul-mob-ic--on' : ''">
                    <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/></svg>
                </div>
                <span>Profile</span>
            </Link>
        </nav>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { Link, usePage } from '@inertiajs/vue3';
import ToastContainer from '@/Components/Toast/ToastContainer.vue';

const props = defineProps({
    pageTitle: { type: String, default: 'Dashboard' },
    pageSection: { type: String, default: '' },
});

const page = usePage();
const drawerOpen = ref(false);
const userMenuOpen = ref(false);
const avatarRef = ref(null);
const logoErr = ref(false);

const authUser    = computed(() => page.props.auth?.user || null);
const wallet      = computed(() => page.props.wallet || null);
const appName     = computed(() => page.props.appName || 'MetaStake');
const logoUrl     = computed(() => page.props.logoUrl || '');
const currencySymbol = computed(() => page.props.currencySymbol || '$');

const userInitials = computed(() => {
    const name = authUser.value?.name || '';
    return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || 'U';
});

const isActive = (url) => {
    if (url === '/user/dashboard') return page.url === url || page.url.startsWith('/user/dashboard');
    if (url === '/user/binary') return page.url === '/user/binary';
    return page.url.startsWith(url);
};

const fmtBalance = (v) => {
    const n = parseFloat(v) || 0;
    if (n >= 1e6) return (n/1e6).toFixed(2) + 'M';
    if (n >= 1e3) return (n/1e3).toFixed(1) + 'K';
    return n.toFixed(2);
};

/* ── SVG icons ── */
const icons = {
    dashboard: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
    investments: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>`,
    finance: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>`,
    earnings: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>`,
    referral: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>`,
    binaryDashboard: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="5" r="2"/><line x1="12" y1="7" x2="12" y2="11"/><line x1="12" y1="11" x2="7" y2="15"/><line x1="12" y1="11" x2="17" y2="15"/><circle cx="7" cy="17" r="2"/><circle cx="17" cy="17" r="2"/></svg>`,
    genealogy: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/><line x1="9" y1="11" x2="9" y2="14"/></svg>`,
    pairings: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="3" width="8" height="6" rx="1"/><rect x="14" y="3" width="8" height="6" rx="1"/><line x1="6" y1="9" x2="6" y2="14"/><line x1="18" y1="9" x2="18" y2="14"/><line x1="6" y1="14" x2="18" y2="14"/><circle cx="12" cy="17" r="3"/></svg>`,
    binaryWallet: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 12V8H6a2 2 0 0 1 0-4h14v4"/><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/><circle cx="16" cy="16" r="1" fill="currentColor"/></svg>`,
    settings: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>`,
    walletBook: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="2" y="5" width="20" height="14" rx="2"/><path d="M2 10h20"/><circle cx="16" cy="15" r="1" fill="currentColor"/></svg>`,
    support: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>`,
    logout: `<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>`,
};

const mainNavItems = [
    { label: 'Dashboard',  url: '/user/dashboard',        icon: icons.dashboard },
    { label: 'Portfolio',  url: '/user/investments',      icon: icons.investments },
    { label: 'Treasury',   url: '/user/finance',          icon: icons.finance },
    { label: 'Rewards',    url: '/user/earnings',         icon: icons.earnings },
    { label: 'Referral',   url: '/user/referral',         icon: icons.referral },
];

const binaryNavItems = [
    { label: 'Binary Dashboard', url: '/user/binary',             icon: icons.binaryDashboard },
    { label: 'Genealogy Tree',   url: '/user/binary/genealogy',   icon: icons.genealogy },
    { label: 'Binary Pairings',  url: '/user/binary/pairings',    icon: icons.pairings },
    { label: 'Binary Wallet',    url: '/user/binary/wallet',      icon: icons.binaryWallet },
];

/* close dropdown on outside click */
const handleOutside = (e) => {
    if (avatarRef.value && !avatarRef.value.contains(e.target)) userMenuOpen.value = false;
};

onMounted(() => document.addEventListener('click', handleOutside));
onUnmounted(() => document.removeEventListener('click', handleOutside));
</script>

<style scoped>
/* ════════════════════════════════════════
   APP SHELL
════════════════════════════════════════ */
.ul-app {
    display: flex;
    min-height: 100vh;
    background: #07030f;
    color: #fff;
    font-family: inherit;
    position: relative;
    overflow-x: hidden;
}

/* ════════════════════════════════════════
   AMBIENT BACKGROUND
════════════════════════════════════════ */
.ul-ambient {
    position: fixed;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    overflow: hidden;
}
.ul-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
}
.ul-orb-1 {
    width: 600px; height: 600px;
    top: -200px; right: 0;
    background: radial-gradient(circle, rgba(167,93,237,0.09) 0%, transparent 70%);
    animation: orbDrift 20s ease-in-out infinite;
}
.ul-orb-2 {
    width: 400px; height: 400px;
    bottom: 0; left: 240px;
    background: radial-gradient(circle, rgba(167,93,237,0.06) 0%, transparent 70%);
    animation: orbDrift 26s ease-in-out infinite reverse;
    animation-delay: -10s;
}
.ul-orb-3 {
    width: 280px; height: 280px;
    top: 40%; right: 20%;
    background: radial-gradient(circle, rgba(232,121,249,0.05) 0%, transparent 70%);
    animation: orbDrift 18s ease-in-out infinite;
    animation-delay: -6s;
}
.ul-grid-dots {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(rgba(167,93,237,0.1) 1px, transparent 1px);
    background-size: 28px 28px;
    opacity: 0.45;
}
@keyframes orbDrift {
    0%,100% { transform: translate(0,0) scale(1); }
    33%      { transform: translate(20px,-30px) scale(1.05); }
    66%      { transform: translate(-15px,20px) scale(0.95); }
}

/* ════════════════════════════════════════
   SIDEBAR
════════════════════════════════════════ */
.ul-sidebar {
    position: fixed;
    top: 0; left: 0; bottom: 0;
    width: 240px;
    z-index: 60;
    display: flex;
    flex-direction: column;
    background: rgba(10,4,22,0.98);
    border-right: 1px solid rgba(167,93,237,0.12);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-width: none;
    transition: transform 0.3s cubic-bezier(0.4,0,0.2,1);
}
.ul-sidebar::-webkit-scrollbar { display: none; }

/* Mobile: sidebar starts off-screen */
@media (max-width: 1023px) {
    .ul-sidebar {
        transform: translateX(-100%);
        box-shadow: 4px 0 40px rgba(0,0,0,0.6);
    }
    .ul-sidebar--open {
        transform: translateX(0);
    }
}

/* ── Logo ── */
.ul-sb-logo {
    padding: 22px 20px 18px;
    border-bottom: 1px solid rgba(167,93,237,0.1);
    flex-shrink: 0;
}
.ul-logo-link {
    display: flex;
    align-items: center;
    gap: 10px;
    text-decoration: none;
}
.ul-logo-img { height: 32px; width: auto; object-fit: contain; }
.ul-logo-text {
    font-size: 18px;
    font-weight: 900;
    background: linear-gradient(135deg, #a75ded, #e879f9);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    letter-spacing: -0.02em;
}

/* ── Nav wrap ── */
.ul-sb-nav-wrap {
    flex: 1;
    padding: 18px 12px 0;
    overflow-y: auto;
    scrollbar-width: none;
}
.ul-sb-nav-wrap::-webkit-scrollbar { display: none; }

.ul-sb-section-lbl {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.14em;
    color: rgba(100,116,139,0.7);
    padding: 0 8px;
    margin-bottom: 6px;
}
.ul-sb-nav { display: flex; flex-direction: column; gap: 2px; }

.ul-sb-item {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 10px 12px;
    border-radius: 11px;
    font-size: 13px;
    font-weight: 600;
    color: #64748b;
    text-decoration: none;
    transition: all 0.22s cubic-bezier(0.4,0,0.2,1);
    position: relative;
    white-space: nowrap;
    border: 1px solid transparent;
}
.ul-sb-item:hover {
    color: #94a3b8;
    background: rgba(167,93,237,0.07);
    border-color: rgba(167,93,237,0.1);
}
.ul-sb-item--active {
    color: #a75ded;
    background: rgba(167,93,237,0.14);
    border-color: rgba(167,93,237,0.25);
    box-shadow: 0 2px 14px rgba(167,93,237,0.08);
}
.ul-sb-item--active::before {
    content: '';
    position: absolute;
    left: 0; top: 6px; bottom: 6px;
    width: 3px;
    border-radius: 0 3px 3px 0;
    background: linear-gradient(to bottom, #a75ded, #e879f9);
}
.ul-sb-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px; height: 18px;
    flex-shrink: 0;
    opacity: 0.85;
}
.ul-sb-item--active .ul-sb-icon { opacity: 1; }
.ul-sb-label { flex: 1; }
.ul-sb-badge {
    font-size: 9px; font-weight: 800;
    padding: 2px 6px; border-radius: 999px;
    background: rgba(167,93,237,0.25); color: #a75ded;
}

/* ── Bottom ── */
.ul-sb-bottom {
    padding: 12px;
    border-top: 1px solid rgba(167,93,237,0.1);
    flex-shrink: 0;
}
.ul-sb-logout {
    display: flex;
    align-items: center;
    gap: 11px;
    padding: 9px 12px;
    border-radius: 10px;
    font-size: 13px;
    font-weight: 600;
    color: #ef4444;
    text-decoration: none;
    transition: all 0.2s;
    margin-bottom: 12px;
    border: 1px solid transparent;
}
.ul-sb-logout:hover {
    background: rgba(239,68,68,0.08);
    border-color: rgba(239,68,68,0.2);
}
.ul-sb-user {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: 12px;
    background: rgba(167,93,237,0.06);
    border: 1px solid rgba(167,93,237,0.15);
}
.ul-sb-avatar {
    width: 34px; height: 34px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed, #a75ded);
    display: flex; align-items: center; justify-content: center;
    font-size: 12px; font-weight: 800; color: #fff;
    flex-shrink: 0;
}
.ul-sb-user-info { flex: 1; min-width: 0; }
.ul-sb-user-name {
    font-size: 12px; font-weight: 700; color: #fff;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ul-sb-user-email {
    font-size: 10px; color: #4b5563;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.ul-sb-user-uid {
    font-size: 10px; color: rgba(167,93,237,0.7);
    font-family: 'Courier New', monospace; letter-spacing: 0.04em;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
    margin-top: 1px;
}

/* ════════════════════════════════════════
   OVERLAY (mobile)
════════════════════════════════════════ */
.ul-overlay {
    position: fixed;
    inset: 0;
    z-index: 55;
    background: rgba(0,0,0,0.6);
    backdrop-filter: blur(4px);
    -webkit-backdrop-filter: blur(4px);
}
.ul-fade-enter-active, .ul-fade-leave-active { transition: opacity 0.3s; }
.ul-fade-enter-from, .ul-fade-leave-to { opacity: 0; }

/* ════════════════════════════════════════
   MAIN AREA
════════════════════════════════════════ */
.ul-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    position: relative;
    z-index: 1;
}
@media (min-width: 1024px) {
    .ul-main { margin-left: 240px; }
}

/* ════════════════════════════════════════
   TOP BAR
════════════════════════════════════════ */
.ul-topbar {
    position: sticky;
    top: 0;
    z-index: 40;
    height: 64px;
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 0 16px;
    background: rgba(7,3,15,0.96);
    border-bottom: 1px solid rgba(167,93,237,0.1);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    flex-shrink: 0;
}
@media (min-width: 640px) { .ul-topbar { padding: 0 24px; } }

.ul-hamburger {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px; height: 38px;
    border-radius: 10px;
    border: 1px solid rgba(167,93,237,0.2);
    background: rgba(167,93,237,0.08);
    color: #a75ded;
    cursor: pointer;
    transition: all 0.2s;
    flex-shrink: 0;
}
.ul-hamburger:hover { background: rgba(167,93,237,0.15); }
@media (min-width: 1024px) { .ul-hamburger { display: none; } }

.ul-tb-title { display: none; }
@media (min-width: 1024px) {
    .ul-tb-title { display: block; }
}
.ul-tb-h1 {
    font-size: 18px;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.02em;
    line-height: 1.1;
}
.ul-tb-sub { font-size: 11px; color: #4b5563; margin-top: 1px; }

.ul-tb-spacer { flex: 1; }

.ul-tb-right {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
}

.ul-status-pill {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 6px 12px;
    border-radius: 999px;
    background: rgba(16,185,129,0.08);
    border: 1px solid rgba(16,185,129,0.2);
}
.ul-status-dot {
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #10b981;
    box-shadow: 0 0 8px rgba(16,185,129,0.9);
    animation: statusPulse 2s ease-in-out infinite;
    flex-shrink: 0;
}
@keyframes statusPulse {
    0%,100% { transform: scale(1); opacity: 1; }
    50%      { transform: scale(1.3); opacity: 0.7; }
}
.ul-status-text { font-size: 11px; font-weight: 700; color: #10b981; display: none; }
@media (min-width: 480px) { .ul-status-text { display: block; } }

.ul-icon-btn {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 38px; height: 38px;
    border-radius: 10px;
    border: 1px solid rgba(167,93,237,0.18);
    background: rgba(167,93,237,0.07);
    color: #94a3b8;
    cursor: pointer;
    transition: all 0.2s;
}
.ul-icon-btn:hover { background: rgba(167,93,237,0.14); color: #a75ded; }
.ul-notif-dot {
    position: absolute;
    top: 8px; right: 8px;
    width: 7px; height: 7px;
    border-radius: 50%;
    background: #e879f9;
    border: 2px solid #07030f;
}

.ul-avatar-wrap { position: relative; }
.ul-avatar-btn { cursor: pointer; }
.ul-tb-avatar {
    width: 38px; height: 38px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed, #a75ded);
    display: flex; align-items: center; justify-content: center;
    font-size: 13px; font-weight: 800; color: #fff;
    border: 2px solid rgba(167,93,237,0.4);
    box-shadow: 0 0 14px rgba(167,93,237,0.3);
    transition: box-shadow 0.2s;
}
.ul-avatar-btn:hover .ul-tb-avatar { box-shadow: 0 0 20px rgba(167,93,237,0.5); }

/* ── User dropdown ── */
.ul-user-drop {
    position: absolute;
    top: calc(100% + 10px);
    right: 0;
    width: 280px;
    background: rgba(10,4,22,0.99);
    border: 1px solid rgba(167,93,237,0.25);
    border-radius: 16px;
    box-shadow: 0 8px 48px rgba(0,0,0,0.7), 0 0 0 1px rgba(167,93,237,0.08);
    overflow: hidden;
    z-index: 100;
}
.ul-drop-enter-active { transition: all 0.2s cubic-bezier(0.34,1.56,0.64,1); }
.ul-drop-leave-active { transition: all 0.15s ease-in; }
.ul-drop-enter-from, .ul-drop-leave-to { opacity: 0; transform: scale(0.94) translateY(-8px); }

.ul-drop-header {
    display: flex; align-items: center; gap: 12px;
    padding: 16px 16px 12px;
    background: rgba(167,93,237,0.07);
    border-bottom: 1px solid rgba(167,93,237,0.12);
}
.ul-drop-avatar {
    width: 40px; height: 40px;
    border-radius: 50%;
    background: linear-gradient(135deg, #7c3aed, #a75ded);
    display: flex; align-items: center; justify-content: center;
    font-size: 14px; font-weight: 800; color: #fff;
    flex-shrink: 0;
}
.ul-drop-name { font-size: 13px; font-weight: 700; color: #fff; }
.ul-drop-email { font-size: 11px; color: #4b5563; }
.ul-drop-uid { font-size: 11px; color: rgba(167,93,237,0.75); font-family: 'Courier New', monospace; letter-spacing: 0.04em; margin-top: 2px; }
.ul-drop-wallet {
    display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
    padding: 12px 16px;
    border-bottom: 1px solid rgba(255,255,255,0.05);
}
.ul-drop-stat {
    padding: 8px 10px;
    border-radius: 10px;
    background: rgba(255,255,255,0.03);
    border: 1px solid rgba(255,255,255,0.06);
    display: flex; flex-direction: column; gap: 2px;
}
.ul-drop-stat-lbl { font-size: 9px; font-weight: 700; color: #4b5563; text-transform: uppercase; letter-spacing: 0.1em; }
.ul-drop-stat-val { font-size: 13px; font-weight: 800; color: #fff; }
.ul-drop-menu { padding: 8px; }
.ul-drop-item {
    display: flex; align-items: center; gap: 10px;
    padding: 9px 10px;
    border-radius: 9px;
    font-size: 13px; font-weight: 600; color: #94a3b8;
    text-decoration: none;
    transition: all 0.2s;
}
.ul-drop-item:hover { background: rgba(167,93,237,0.08); color: #fff; }
.ul-drop-logout { color: #ef4444; }
.ul-drop-logout:hover { background: rgba(239,68,68,0.08); color: #ef4444; }
.ul-drop-divider { height: 1px; background: rgba(255,255,255,0.06); margin: 4px 0; }

/* ════════════════════════════════════════
   CONTENT AREA
════════════════════════════════════════ */
.ul-content {
    flex: 1;
    position: relative;
    z-index: 1;
}
@media (max-width: 1023px) {
    .ul-content { padding-bottom: 76px; }
}

/* ════════════════════════════════════════
   MOBILE BOTTOM NAV
════════════════════════════════════════ */
.ul-mob-nav {
    position: fixed;
    bottom: 0; left: 0; right: 0;
    z-index: 100;
    display: flex;
    align-items: flex-end;
    justify-content: space-around;
    background: rgba(7,3,15,0.97);
    backdrop-filter: blur(28px);
    -webkit-backdrop-filter: blur(28px);
    border-top: 1px solid rgba(167,93,237,0.18);
    padding: 8px 4px env(safe-area-inset-bottom, 0px);
}
@media (min-width: 1024px) { .ul-mob-nav { display: none; } }

.ul-mob-tab {
    display: flex; flex-direction: column; align-items: center;
    gap: 3px; min-width: 52px; padding: 2px 4px;
    padding-bottom: max(10px, env(safe-area-inset-bottom, 10px));
    text-decoration: none; font-size: 9px; font-weight: 700;
    color: #374151; text-transform: uppercase; letter-spacing: 0.08em;
    transition: color 0.2s;
}
.ul-mob-tab--on { color: #a75ded; }

.ul-mob-ic {
    width: 36px; height: 36px; border-radius: 11px;
    display: flex; align-items: center; justify-content: center;
    color: #374151; transition: all 0.2s;
}
.ul-mob-ic--on {
    color: #a75ded;
    background: rgba(167,93,237,0.15);
    box-shadow: 0 0 12px rgba(167,93,237,0.3);
}
.ul-mob-tab:active .ul-mob-ic { transform: scale(0.88); }

.ul-mob-tab--plus { padding-top: 0; }
.ul-mob-plus {
    width: 50px; height: 50px; border-radius: 50%;
    display: flex; align-items: center; justify-content: center;
    background: linear-gradient(135deg, #7c3aed, #a75ded);
    color: #fff; margin-bottom: 2px;
    transition: transform 0.2s;
    box-shadow: 0 0 0 4px rgba(167,93,237,0.14), 0 0 22px rgba(167,93,237,0.44);
    animation: ulPlusGlow 3s ease-in-out infinite;
}
.ul-mob-tab--plus:active .ul-mob-plus { transform: scale(0.92); }
@keyframes ulPlusGlow {
    0%,100% { box-shadow: 0 0 0 4px rgba(167,93,237,0.14), 0 0 22px rgba(167,93,237,0.4); }
    50%      { box-shadow: 0 0 0 7px rgba(167,93,237,0.1), 0 0 34px rgba(167,93,237,0.58); }
}

/* ════════════════════════════════════════
   SCROLLBAR
════════════════════════════════════════ */
::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: rgba(167,93,237,0.3); border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: rgba(167,93,237,0.5); }

/* ════════════════════════════════════════
   FOCUS & A11Y
════════════════════════════════════════ */
:focus-visible { outline: 2px solid rgba(167,93,237,0.6); outline-offset: 2px; border-radius: 6px; }

/* ════════════════════════════════════════
   REDUCED MOTION
════════════════════════════════════════ */
@media (prefers-reduced-motion: reduce) {
    .ul-orb, .ul-status-dot { animation: none !important; }
    .ul-sidebar { transition: none; }
}
</style>
